import { createBrowserRouter, type RouteObject } from 'react-router';
import { INITIAL_LOCALE, ROUTER_BASENAME } from '@/client/i18n/locale';
import { logicalRoutePattern } from '@/lib/seo';
import type { Locale } from '@/types';
import { RootLayout } from '@/layouts/RootLayout';
import { HomePage } from '@/pages/HomePage';
import { ShopPage } from '@/pages/ShopPage';
import { FindsPage } from '@/pages/FindsPage';
import { ProductDetailPage } from '@/pages/ProductDetailPage';
import { PackPage } from '@/pages/PackPage';
import { CartPage } from '@/pages/CartPage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { CheckoutReturnPage } from '@/pages/CheckoutReturnPage';
import { AboutPage, PhilosophyPage, ServicesPage } from '@/pages/CaroPages';
import { TheEditPage, ArticlePage } from '@/pages/TheEditPage';
import { ContactPage } from '@/pages/ContactPage';
import { NewsletterPage } from '@/pages/NewsletterPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteLoadError } from '@/components/RouteLoadError';

// Route table with LOGICAL (Spanish) paths, exported so component tests can
// build a `createMemoryRouter` from the same definitions the app uses. The app
// mounts routesFor(locale): the same table with EN/FR path segments translated.
export const routes: RouteObject[] = [
  {
    // Admin. Protected by Cloudflare Access at the edge in production. Lazy: the
    // whole panel (tabs, editors) is its own chunk, so store visitors never
    // download it (it was most of the main bundle).
    path: '/admin',
    lazy: { Component: async () => (await import('@/layouts/AdminLayout')).AdminLayout },
    errorElement: <RouteLoadError />,
    children: [
      { index: true, lazy: { Component: async () => (await import('@/pages/AdminPage')).AdminPage } },
      // Edit preview (scheduled articles included); behind Access like all /admin*.
      {
        path: 'edit/:slug',
        lazy: { Component: async () => (await import('@/pages/admin/ArticlePreview')).ArticlePreview },
      },
      // La carta: the web page of a draft as it will be published (docs/plan-cartas.md, fase 4).
      {
        path: 'cartas/:id',
        lazy: { Component: async () => (await import('@/pages/admin/LetterPreview')).LetterPreview },
      },
    ],
  },
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/shop', element: <ShopPage /> },
      // Static segment: ranks above :category (Estilo's one-off pieces).
      { path: '/shop/hallazgos', element: <FindsPage /> },
      // Static segment too: a pack page (docs/plan-packs.md) ranks above :category/:slug.
      { path: '/shop/pack/:slug', element: <PackPage /> },
      { path: '/shop/:category', element: <ShopPage /> },
      { path: '/shop/:category/:slug', element: <ProductDetailPage /> },
      { path: '/cart', element: <CartPage /> },
      { path: '/checkout', element: <CheckoutPage /> },
      { path: '/checkout/return', element: <CheckoutReturnPage /> },
      { path: '/caro', element: <AboutPage /> },
      { path: '/caro/filosofia', element: <PhilosophyPage /> },
      { path: '/caro/servicios', element: <ServicesPage /> },
      // One page per service (docs/plan-tests-y-servicios.md).
      {
        path: '/caro/servicios/:slug',
        lazy: { Component: async () => (await import('@/pages/ServicePage')).ServicePage },
        errorElement: <RouteLoadError />,
      },
      { path: '/the-edit', element: <TheEditPage /> },
      { path: '/the-edit/post/:slug', element: <ArticlePage /> },
      { path: '/the-edit/:category', element: <TheEditPage /> },
      { path: '/contacto', element: <ContactPage /> },
      { path: '/newsletter', element: <NewsletterPage /> },
      // A sent letter of La carta (docs/plan-cartas.md). Lazy: its own small chunk.
      {
        path: '/newsletter/:year/:month/:slug',
        lazy: { Component: async () => (await import('@/pages/LetterPage')).LetterPage },
        errorElement: <RouteLoadError />,
      },
      // The style tests (docs/plan-tests-y-servicios.md): the hub, then each game. Lazy, so the games and
      // their data stay out of the main bundle.
      {
        path: '/estudio-de-estilo',
        lazy: { Component: async () => (await import('@/features/colorimetry/TestsHubPage')).TestsHubPage },
        errorElement: <RouteLoadError />,
      },
      // The guide «Combinar colores» (docs/plan-estudio-p1-guia.md): lazy, its data stays out of the main bundle.
      {
        path: '/estudio-de-estilo/combinar-colores',
        lazy: { Component: async () => (await import('@/features/studio/GuidePage')).GuidePage },
        errorElement: <RouteLoadError />,
      },
      // «Mi estudio» (docs/plan-estudio-p2-muneco.md): your doll in `?p=`, noindex. Lazy like the guide.
      {
        path: '/estudio-de-estilo/mi-estudio',
        lazy: { Component: async () => (await import('@/features/studio/MyStudioPage')).MyStudioPage },
        errorElement: <RouteLoadError />,
      },
      // COLORIMETRY ME! (ES/EN/FR).
      {
        path: '/estudio-de-estilo/colorimetria',
        lazy: { Component: async () => (await import('@/features/colorimetry/ColorimetryPage')).ColorimetryPage },
        errorElement: <RouteLoadError />,
      },
      // P2 (docs/plan-colorimetry-me-p2.md): the silhouette and face games (light landings; the game loads
      // on «Empezar») and one page per result. Held noindex until STYLE_INDEXABLE.
      {
        path: '/estudio-de-estilo/morfologia',
        lazy: { Component: async () => (await import('@/features/colorimetry/StyleLanding')).SilhouettePage },
        errorElement: <RouteLoadError />,
      },
      {
        path: '/estudio-de-estilo/forma-de-cara',
        lazy: { Component: async () => (await import('@/features/colorimetry/StyleLanding')).FacePage },
        errorElement: <RouteLoadError />,
      },
      {
        path: '/estudio-de-estilo/morfologia/:slug',
        lazy: { Component: async () => (await import('@/features/colorimetry/StyleResultPage')).ShapeResultPage },
        errorElement: <RouteLoadError />,
      },
      {
        path: '/estudio-de-estilo/forma-de-cara/:slug',
        lazy: { Component: async () => (await import('@/features/colorimetry/StyleResultPage')).FaceResultPage },
        errorElement: <RouteLoadError />,
      },
      // The page behind a shared result: one per season, with its own social card.
      {
        path: '/estudio-de-estilo/colorimetria/:slug',
        lazy: { Component: async () => (await import('@/features/colorimetry/SeasonSharePage')).SeasonSharePage },
        errorElement: <RouteLoadError />,
      },
      // Lazy: the legal texts (ES + FR, ~40 KB gzipped) stay out of the main
      // bundle; only visitors who open a legal page download them.
      {
        path: '/legal/:slug',
        lazy: { Component: async () => (await import('@/pages/LegalPage')).LegalPage },
        // A stale chunk after a deploy fails to import: reload once into the new build.
        errorElement: <RouteLoadError />,
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // EN/FR mount under /en, /fr via the router basename (see i18n/locale.ts),
  // with routesFor(locale) translating the path segments below it.
];

/**
 * The route table for `locale`: every absolute path translated with
 * logicalRoutePattern (`/shop/:category` → fr `/boutique/:category`); ES is the
 * table itself. One route per logical page: old Spanish URLs under /en, /fr are
 * NOT aliased here (the Worker 301s them). Pages read logical params with
 * useLogicalParams. Returns fresh objects (`lazy` included: React Router mutates it).
 */
export function routesFor(locale: Locale, rs: RouteObject[] = routes): RouteObject[] {
  return rs.map((r) => {
    const copy = { ...r } as RouteObject;
    if (r.path?.startsWith('/')) copy.path = logicalRoutePattern(locale, r.path);
    if (r.children) copy.children = routesFor(locale, r.children);
    if (r.lazy && typeof r.lazy === 'object') copy.lazy = { ...r.lazy };
    return copy;
  });
}

export const router = createBrowserRouter(routesFor(INITIAL_LOCALE), { basename: ROUTER_BASENAME });
